import { Analytics } from '@cobliteam/analytics';
import { ChecklistFieldType } from '@cobliteam/api-lib';
import { useTranslation } from '@cobliteam/i18n';
import { Typography } from '@cobliteam/ui/components';
import { FormField, FormItem, FormLabel, Select, SelectContent, SelectItem, SelectTrigger, useFormContext } from '@cobliteam/ui/form';
import ChecklistIcon from '../../../../components/ChecklistIcon';
import type { FormValues } from '../../checklistSchema';

interface ChecklistItemProps {
  index: number;
}

const ChecklistItemType = ({ index }: ChecklistItemProps) => {
  const { t } = useTranslation('checklists');
  const { control, watch, clearErrors } = useFormContext<FormValues>();
  const itemType = watch(`items.${index}.type`);

  const text = {
    [ChecklistFieldType.TEXT]: t('text_field'),
    [ChecklistFieldType.CHECK]: t('mark_as_done'),
    [ChecklistFieldType.MULTI_SELECT]: t('multiple_choice'),
    [ChecklistFieldType.SINGLE_SELECT]: t('single_selection'),
  };

  return (
    <FormField control={control} name={`items.${index}.type`}>
      {({ field }) => (
        <FormItem className='flex items-center gap-2.5'>
          <Select
            defaultValue={field.value}
            onValueChange={(value) => {
              Analytics.track('Checklist Item Type Changed', {
                type: value,
              });

              const isSelectItemType = itemType === ChecklistFieldType.SINGLE_SELECT || itemType === ChecklistFieldType.MULTI_SELECT;
              const isSelectValue = value === ChecklistFieldType.SINGLE_SELECT || value === ChecklistFieldType.MULTI_SELECT;
              if (!isSelectItemType && isSelectValue) clearErrors(`items.${index}.content.options`);
              field.onChange(value);
            }}
          >
            <SelectTrigger className='h-8 w-fit border-gray-lightest bg-gray-lightest'>
              <ChecklistIcon type={itemType} className='text-primary' />
            </SelectTrigger>
            <SelectContent className='z-drawer'>
              {Object.values(ChecklistFieldType).map((type) => (
                <SelectItem key={type} value={type} disabled={field.value === type}>
                  <div className='flex items-center gap-2.5 text-secondary-dark text-sm'>
                    <ChecklistIcon type={type} />
                    {text[type]}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormLabel>
            <Typography variant='caption-default' color='secondary'>
              {text[field.value]} {t('selected')}
            </Typography>
          </FormLabel>
        </FormItem>
      )}
    </FormField>
  );
};

export default ChecklistItemType;
